/**
 * #464 — an Agent's quiet hours as the Edit form sees them. Pure (no React) so the
 * load/save mapping is unit-tested. Silent weekly windows in an IANA zone; the
 * bridge drops an inbound that arrives inside one (no reply, no away message).
 */
import type { MyBridgeAgent, MyBridgeAgentInput, MyBridgeQuietDay, MyBridgeQuietHours, MyBridgeQuietWindow } from '@common/reducers/backend-api/backend-api';

export const QUIET_DAYS: readonly MyBridgeQuietDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** Same cap the bridge enforces. */
export const QUIET_HOURS_MAX_WINDOWS = 14;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The slice of the form this module maps. */
export interface QuietHoursFormFields {
  quietEnabled: boolean;
  /** IANA zone, e.g. America/Maceio. */
  quietTz: string;
  quietWindows: MyBridgeQuietWindow[];
}

export const isValidTimeZone = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/** The browser's zone, or UTC where it cannot be read. */
export const browserTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : 'UTC';
  } catch {
    return 'UTC';
  }
};

export const newQuietWindow = (): MyBridgeQuietWindow => ({ days: ['mon', 'tue', 'wed', 'thu', 'fri'], from: '17:00', to: '22:00' });

export function quietHoursFormFrom(agent: Pick<MyBridgeAgent, 'quietHours'> | null | undefined): QuietHoursFormFields {
  const stored = agent?.quietHours;
  if (!stored || stored.windows.length === 0) return { quietEnabled: false, quietTz: browserTimeZone(), quietWindows: [] };
  return {
    quietEnabled: true,
    quietTz: stored.tz,
    quietWindows: stored.windows.map((w) => ({ days: [...w.days], from: w.from, to: w.to })),
  };
}

/** The first problem with the quiet-hours part of the form, as an i18n key; null when fine (or off). */
export function quietHoursError(f: QuietHoursFormFields): 'tz' | 'empty' | 'days' | 'time' | 'same' | null {
  if (!f.quietEnabled) return null;
  if (!isValidTimeZone(f.quietTz)) return 'tz';
  if (f.quietWindows.length === 0) return 'empty';
  for (const w of f.quietWindows) {
    if (w.days.length === 0) return 'days';
    if (!HHMM.test(w.from) || !HHMM.test(w.to)) return 'time';
    if (w.from === w.to) return 'same';
  }
  return null;
}

/** On = the form's rule (days in week order); off = null. */
const valueOf = (f: QuietHoursFormFields): MyBridgeQuietHours | null =>
  f.quietEnabled && f.quietWindows.length > 0
    ? {
        tz: f.quietTz,
        windows: f.quietWindows.map((w) => ({ days: QUIET_DAYS.filter((d) => w.days.includes(d)), from: w.from, to: w.to })),
      }
    : null;

/**
 * The save payload: ONLY when it changed against what was loaded, so an untouched
 * form never rewrites the rule (and never trips a backend that predates it).
 * Turning it off sends `null`.
 */
export function quietHoursPayload(loaded: QuietHoursFormFields, form: QuietHoursFormFields): Pick<MyBridgeAgentInput, 'quietHours'> {
  const next = valueOf(form);
  if (JSON.stringify(next) === JSON.stringify(valueOf(loaded))) return {};
  return { quietHours: next };
}
